"use client";

import { useState, useEffect } from "react";
import { useScrollAnimate } from "../lib/hooks";

const TEXT = "#EDEDED";
const TEAL = "#4FCDC4";
const MK_BG = "#0f0f11";
const MK_CARD = "rgba(255,255,255,0.04)";
const MK_BD = "rgba(255,255,255,0.06)";
const MK_TEXT = "#a0a0a0";

function Megaphone({ size = 14, color }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color || "currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m3 11 18-5v12L3 14v-3z" />
      <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
    </svg>
  );
}

export default function ChatGPTAdsCampaignWizardMockup() {
  const [ref, scrolled] = useScrollAnimate();
  const [open, setOpen] = useState(false);
  const [showCallout, setShowCallout] = useState(false);

  useEffect(() => {
    if (!scrolled) return;
    const t1 = setTimeout(() => setOpen(true), 600);
    const t2 = setTimeout(() => setShowCallout(true), 1400);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [scrolled]);

  return (
    <div ref={ref} style={{
      maxWidth: 720, margin: "24px auto",
      background: MK_BG, borderRadius: 12,
      border: `1px solid ${MK_BD}`,
      overflow: "hidden",
      opacity: scrolled ? 1 : 0,
      transform: scrolled ? "translateY(0)" : "translateY(8px)",
      transition: "opacity 0.4s ease, transform 0.4s ease",
      fontFamily: "var(--body)",
    }}>
      {/* Header bar */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "14px 18px",
        borderBottom: `1px solid ${MK_BD}`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, color: TEXT }}>
          <Megaphone size={14} />
          <span style={{ fontSize: 13, fontWeight: 600 }}>New campaign</span>
        </div>
        <span style={{ fontSize: 16, color: MK_TEXT, lineHeight: 1 }} aria-hidden="true">×</span>
      </div>

      {/* Body grid */}
      <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", minHeight: 320 }}>
        {/* Left rail */}
        <div style={{ padding: "20px 14px", borderRight: `1px solid ${MK_BD}` }}>
          <Step n={1} active label="Create Campaign" />
          <SubItem label="CPC campaign" />
          <Step n={2} label="Create Ad Group & Ads" />
          <Step n={3} label="Review" />
        </div>

        {/* Right pane */}
        <div style={{ padding: "20px 24px" }}>
          <FormField label="Campaign name">
            <Input value="CPC campaign" />
          </FormField>

          <FormField label="Objective">
            <DropdownClosed value="Clicks" focused={open} />
            <DropdownMenu open={open} showCallout={showCallout} />
          </FormField>

          <div style={{ marginTop: 16, opacity: 0.5, pointerEvents: "none" }}>
            <PreviewRow label="Budget" value="Campaign budget" />
            <PreviewRow label="Start date" value="Apr 21, 2026" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Step({ n, active, label }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      marginBottom: active ? 8 : 14,
    }}>
      <div style={{
        width: 22, height: 22, borderRadius: "50%",
        background: active ? "#000" : "rgba(255,255,255,0.08)",
        color: active ? "#fff" : MK_TEXT,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 11, fontWeight: 600, flexShrink: 0,
      }}>{n}</div>
      <span style={{ fontSize: 12, color: active ? TEXT : MK_TEXT, fontWeight: active ? 600 : 500 }}>{label}</span>
    </div>
  );
}

function SubItem({ label }) {
  return (
    <div style={{
      marginLeft: 32, padding: "6px 10px",
      background: "rgba(255,255,255,0.05)",
      borderRadius: 6, marginBottom: 14,
      display: "flex", alignItems: "center", gap: 8,
    }}>
      <Megaphone size={11} color={MK_TEXT} />
      <span style={{ fontSize: 11, color: TEXT }}>{label}</span>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div style={{ marginBottom: 14, position: "relative" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: TEXT, marginBottom: 6 }}>{label}</div>
      {children}
    </div>
  );
}

function Input({ value }) {
  return (
    <div style={{
      background: MK_CARD, border: `1px solid ${MK_BD}`,
      borderRadius: 6, padding: "8px 12px",
      fontSize: 13, color: TEXT,
    }}>{value}</div>
  );
}

function DropdownClosed({ value, focused }) {
  return (
    <div style={{
      background: MK_CARD,
      border: `1px solid ${focused ? "rgba(79,205,196,0.5)" : MK_BD}`,
      borderRadius: 6, padding: "8px 12px",
      fontSize: 13, color: TEXT,
      display: "flex", alignItems: "center", justifyContent: "space-between",
      transition: "border-color 0.2s ease",
    }}>
      <span>{value}</span>
      <span style={{ fontSize: 10, color: MK_TEXT }} aria-hidden="true">▾</span>
    </div>
  );
}

function DropdownMenu({ open, showCallout }) {
  return (
    <div style={{
      position: "absolute",
      top: "100%", left: 0, right: 0,
      marginTop: 4,
      background: MK_BG,
      border: `1px solid ${MK_BD}`,
      borderRadius: 6,
      overflow: "visible",
      boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
      zIndex: 2,
      opacity: open ? 1 : 0,
      transform: open ? "translateY(0)" : "translateY(-4px)",
      pointerEvents: open ? "auto" : "none",
      transition: "opacity 0.2s ease, transform 0.2s ease",
    }}>
      <DropdownItem label="Reach" checked />
      <DropdownItem label="Clicks" hover />
      <DropdownItem label="Conversions" disabled callout={showCallout} />
    </div>
  );
}

function DropdownItem({ label, checked, hover, disabled, callout }) {
  return (
    <div style={{
      padding: "8px 12px",
      fontSize: 13,
      color: disabled ? MK_TEXT : TEXT,
      opacity: disabled ? 0.55 : 1,
      background: hover ? "rgba(255,255,255,0.04)" : "transparent",
      display: "flex", alignItems: "center", justifyContent: "space-between",
      gap: 8, position: "relative",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {checked
          ? <span style={{ color: TEXT, width: 12, textAlign: "center" }} aria-hidden="true">✓</span>
          : <span style={{ width: 12 }} aria-hidden="true" />}
        <span>{label}</span>
        {disabled && (
          <span style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 14, height: 14, borderRadius: "50%",
            border: `1px solid ${MK_TEXT}`,
            fontSize: 9, color: MK_TEXT, marginLeft: 4,
          }} aria-label="Info">i</span>
        )}
      </div>
      {callout && disabled && (
        <span style={{
          background: "rgba(79,205,196,0.12)",
          border: "1px solid rgba(79,205,196,0.25)",
          borderRadius: 4,
          padding: "2px 8px",
          fontSize: 10,
          color: TEAL,
          fontWeight: 600,
          whiteSpace: "nowrap",
          opacity: callout ? 1 : 0,
          transform: callout ? "translateX(0)" : "translateX(6px)",
          transition: "opacity 0.3s ease, transform 0.3s ease",
        }}>
          Disabled in V1
        </span>
      )}
    </div>
  );
}

function PreviewRow({ label, value }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: TEXT, marginBottom: 4 }}>{label}</div>
      <div style={{
        background: MK_CARD, border: `1px solid ${MK_BD}`,
        borderRadius: 6, padding: "6px 10px",
        fontSize: 11, color: MK_TEXT,
      }}>{value}</div>
    </div>
  );
}
